import { Component, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { Router } from '@angular/router';

import { EntitlementService } from '../../core/services/entitlement.service';
import { SubscriptionBannerViewModel } from '../../core/services/subscription-banner.util';
import { PermissionService } from '../../core/services/permission.service';
import { TabCoreScreens } from '../../core/const/constants';

/**
 * Persistent, tenant-wide notice of subscription trouble - the thing the customer never saw before this
 * component existed. The backend already resolves PastDue -> Grace -> Degraded correctly and ships a
 * status DTO built for exactly this; this component is the first thing that reads it instead of
 * discarding it.
 *
 * Lives in the app shell (layout.component.html), above the router outlet, so it is visible on every
 * screen rather than buried on a billing page the customer has no reason to open. Renders nothing for a
 * healthy subscription (Trialing, Active) - see subscription-banner.util for why each state gets the
 * treatment it gets.
 */
@Component({
    selector: 'app-subscription-status-banner',
    imports: [NgIf],
    templateUrl: './subscription-status-banner.component.html',
    styleUrl: './subscription-status-banner.component.scss'
})
export class SubscriptionStatusBannerComponent {
    private readonly entitlementService: EntitlementService = inject(EntitlementService);
    private readonly permissionService: PermissionService = inject(PermissionService);
    private readonly router: Router = inject(Router);

    /**
     * Re-read on every change detection pass rather than cached once. Entitlements load asynchronously
     * (route resolver / app bootstrap) and can change mid-session, and this getter is cheap - a handful
     * of comparisons, no HTTP call - so there is no reason to hand-roll a subscription for it.
     */
    get banner(): SubscriptionBannerViewModel | null {
        return this.entitlementService.banner;
    }

    /**
     * Only a Tenant/TAB Administrator (or App Administrator) can actually reach the billing screens. A
     * link a regular user cannot use is worse than no link, so the CTA becomes plain text for anyone else.
     */
    get canManageBilling(): boolean {
        return this.permissionService.doesUserIsTenantAdministrator()
            || this.permissionService.doesUserIsTabAdministrator()
            || this.permissionService.doesUserIsAppAdmin();
    }

    goToYourProducts(): void {
        this.router.navigate([`/admin/${TabCoreScreens.TabYourProductsScreen}`]);
    }
}
